/**
 * @fileoverview Modal component
 * @author Matt Convente (mconvente@yext.com)
 */
// @ts-nocheck

import {DeprecatedOldButton as Button} from '/ui/components/button/button';
import {classNames} from '/ui/lib/classnames';
import {TopLevelContainerContext} from '/ui/lib/containers';
import {logError} from '/ui/lib/errors';
import {yext} from '/ui/lib/msg';
import {useUid} from '/ui/lib/uid';

import * as styles from '/ui/components/modal/modal.module.scss';
import MediumXSvg from '/ui/icons/inline/medium-x.svg';

const {FocusScope} = /** @type {typeof _react_aria$focus} */ (require('@react-aria/focus'));
const AriaModal = /** @type {ReactAriaModalJS} */ (require('react-aria-modal'));

/**
 * @typedef {{
 *   children?: React.ReactNode,
 *   onHideModal: (function(): void),
 *   dialogSize: string,
 *   show?: boolean,
 *   callToAction?: !Object,
 *   tid?: string,
 *   title: (string|React.ReactElement),
 *   hideTitle?: boolean,
 *   hideCloseButton?: boolean,
 *   zIndex?: number,
 *   pinToTop?: boolean,
 *   enableBackgroundScroll?: boolean,
 * }}
 */
export let ModalProps;

/**
 * @param {!ModalProps} props
 * @returns {React.ReactNode}
 */
export function Modal({
  children,
  onHideModal,
  dialogSize,
  show = false,
  callToAction,
  tid,
  title,
  hideTitle = false,
  hideCloseButton = false,
  zIndex,
  pinToTop = false,
  enableBackgroundScroll = false,
}) {
  if (hideTitle && typeof title !== 'string') {
    logError('Modal title must be a string if hideTitle is used');
  }

  const shouldShowTitle = title && !hideTitle;
  const shouldShowCloseButton = !hideCloseButton;
  const uid = useUid();
  /** @type {!React.MutableRefObject<?HTMLDivElement>} */
  const containerRef = React.useRef(null);

  const modalClasses = classNames(
    styles.modal,
    {
      [styles.modalSmall]: dialogSize === 'small',
      [styles.modalLarge]: dialogSize === 'large',
      [styles.modalFullwidth]: dialogSize === 'fullwidth',
      [styles.modalFullpage]: dialogSize === 'fullpage',
    },
  );

  const headerClasses = classNames(
    styles.header,
    {
      [styles.headerSmall]: dialogSize === 'small',
      [styles.headerLarge]: dialogSize === 'large',
      [styles.headerFullwidth]: dialogSize === 'fullwidth',
      [styles.headerFullpage]: dialogSize === 'fullpage',
    },
  );

  const titleClass = classNames(
    {
      [styles.titleSmall]: dialogSize === 'small',
      [styles.titleLarge]: dialogSize === 'large',
      [styles.titleFullwidth]: dialogSize === 'fullwidth',
      [styles.titleFullpage]: dialogSize === 'fullpage',
    },
  );

  const modalMarkup = show ? (
    ReactDOM.createPortal(
      <AriaModal
        titleId={hideTitle ? undefined : `dialog-${uid}-title`}
        titleText={(hideTitle && typeof title === 'string') ? title : undefined}
        onExit={onHideModal}
        verticallyCenter={!pinToTop}
        underlayColor={'rgba(49, 58, 71, 0.45)'}
        underlayStyle={{boxSizing: 'border-box', padding: pinToTop ? '6.25rem 1rem 1rem' : '1rem', zIndex: '10'}}
        underlayClickExits={false}
        dialogStyle={{zIndex: '11'}}
        focusTrapOptions={{
          'clickOutsideDeactivates': true,
          'delayInitialFocus': false,
          'fallbackFocus': `#dialog-${uid}`,
        }}
        focusTrapPaused
        scrollDisabled={!enableBackgroundScroll}
      >
        <FocusScope contain restoreFocus>
          <div
            className={modalClasses}
            style={{zIndex}}
            tid={tid}
            // FocusTrap needs a focusable element, otherwise it throws an error.
            // Using this div as fallback to prevent no focusable element error.
            tabIndex={-1}
            id={`dialog-${uid}`}
            ref={containerRef}
          >
            <TopLevelContainerContext.Provider value={containerRef}>
              {(shouldShowTitle || shouldShowCloseButton)
                && <div
                  className={headerClasses}
                >
                  {shouldShowCloseButton
                    && <CloseButton onClick={onHideModal} ariaLabel={yext.msg('Close Modal')} />
                  }
                  {shouldShowTitle
                    && <div
                      className={titleClass}
                      id={`dialog-${uid}-title`}
                      // eslint-disable-next-line jsx-a11y/role-has-required-aria-props
                      role="heading"
                    >
                      {title}
                    </div>
                  }
                </div>
              }
              {(dialogSize === 'small')
                ? <div className={styles.content}>{children}</div>
                : children}
              {callToAction
                && <div className={styles.footer}>
                  {renderFooter(callToAction, onHideModal)}
                </div>
              }
            </TopLevelContainerContext.Provider>
          </div>
        </FocusScope>
      </AriaModal>,
      document.body,
    )
  ) : null;

  return modalMarkup;
}

/**
 * @suppress {checkTypes}
 * @param {!Object} callToAction object that contains the actions with which
 * to construct the buttons
 * @param {function(): void} hideModal function that hides the modal
 * @return {React.ReactElement} buttons in the footer
 */
function renderFooter(callToAction, hideModal) {
  return (
    <>
      {callToAction.cancel && getButtonFromAction(callToAction.cancel, hideModal)}
      {callToAction.secondary && getButtonFromAction(callToAction.secondary, hideModal)}
      {callToAction.primary && getButtonFromAction(callToAction.primary, hideModal)}
    </>
  );
}

/**
 * @suppress {checkTypes}
 * @param {!Object} action action object that contains info for the button
 * @param {function(): void} hideModal function that hides the modal, to be used as
 * the default onClick if they do not specify a callback
 * @return {React.ReactElement} the constructed call to action button
 */
function getButtonFromAction(action, hideModal) {
  if (React.isValidElement(action)) {
    return action;
  }
  const isChromeless = action.type === 'cancel';
  return <Button
    onClick={action.callback || hideModal}
    chromeless={isChromeless}
    padding={isChromeless ? '0 4px' : undefined}
    context={getButtonContextFromActionType(action.type)}
    id={action.type}
    disabled={action.disabled}
    tid={action.tid}
    pendoId={action.pendoId}
  >
    {action.text}
  </Button>;
}

/**
 * @param {string} type the type of button needed
 * @return {string} the type of button in the naming format
 * required by the Button constructor
 */
function getButtonContextFromActionType(type) {
  switch (type) {
    case 'active':
      return 'primary';
    case 'destructive':
      return 'warning';
    default:
      return 'default';
  }
}

export function CloseButton({onClick, ariaLabel}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={styles.closeButton}
      aria-label={ariaLabel}
    >
      {/* medium-x.svg is resized to 16x16 */}
      <MediumXSvg height={null} width={null} viewBox="0 0 20 20" />
    </button>
  );
}
